/**
 * The daily execution / blocked-source series behind the chart, the details rail and
 * the two window KPIs.
 *
 * One request, not two: every window figure here is a slice of one series, so
 * fetching twice the selected window reproduces both halves and the "vs prev period"
 * deltas fall out of arithmetic. The ticket is explicit that these keep "the existing
 * logic from the current feature", and `GET /rules/executions` already accepts an
 * arbitrary `from`/`to`.
 */
import { RuleExecutionsResponseDTO } from 'api-models'
import moment from 'moment'
import { ReactNode, createContext, useContext } from 'react'
import { RuleActivity } from './types'

export interface RuleActivityWindow {
	/** Day labels covering TWICE the selected time frame, oldest first, dense — a day
	 *  the rule never fired on is a labelled zero, not a gap. */
	days: string[]
	/** The same days carrying their year, for tooltips — its own series, because a
	 *  30-day window can straddle December. */
	dayTitles: string[]
	/** One dense entry per rule that has any history in the window. */
	activity: RuleActivity[]
}

const EMPTY_WINDOW: RuleActivityWindow = {
	days: [],
	dayTitles: [],
	activity: []
}

const RuleActivityContext = createContext<RuleActivityWindow>(EMPTY_WINDOW)

export const RuleActivityProvider = ({
	value,
	children
}: {
	value: RuleActivityWindow
	children: ReactNode
}) => (
	<RuleActivityContext.Provider value={value}>
		{children}
	</RuleActivityContext.Provider>
)

export const useRuleActivity = () => useContext(RuleActivityContext)

/** The window to ask the API for: twice the time frame, ending today. */
export const activityRange = (days: number) => {
	const to = moment().endOf('day')
	const from = moment()
		.subtract(days * 2 - 1, 'days')
		.startOf('day')
	return { from: from.valueOf(), to: to.valueOf() }
}

const LABEL_FORMAT = 'MMM D'
const TITLE_FORMAT = 'MMM D, YYYY'
const KEY_FORMAT = 'YYYY-MM-DD'

/** Turns the endpoint's sparse per-rule `dailyCounts` into dense arrays: every
 *  consumer treats index N as "N days ago", so a sparse array would draw Tuesday's
 *  spike on Sunday. */
export const buildRuleActivity = (
	data: RuleExecutionsResponseDTO | undefined,
	days: number
): RuleActivityWindow => {
	const span = days * 2
	const start = moment()
		.startOf('day')
		.subtract(span - 1, 'days')

	const labels: string[] = []
	const titles: string[] = []
	const keyToIndex = new Map<string, number>()
	for (let i = 0; i < span; i++) {
		const day = start.clone().add(i, 'days')
		labels.push(day.format(LABEL_FORMAT))
		titles.push(day.format(TITLE_FORMAT))
		keyToIndex.set(day.format(KEY_FORMAT), i)
	}

	const densify = (counts?: Array<{ date: string; count: number }>) => {
		const series = new Array<number>(span).fill(0)
		counts?.forEach(({ date, count }) => {
			const index = keyToIndex.get(moment(date).format(KEY_FORMAT))
			// A day outside the requested window is dropped rather than clamped
			// into the nearest slot, which would inflate the edge bars.
			if (index !== undefined) series[index] = count
		})
		return series
	}

	return {
		days: labels,
		dayTitles: titles,
		activity: (data?.rules ?? []).map((rule) => ({
			ruleId: rule.ruleId,
			executions: densify(rule.dailyCounts),
			blockedSources: densify(rule.dailyBlockedSources)
		}))
	}
}

const sum = (values: number[]) => values.reduce((total, n) => total + n, 0)

/** Current and preceding window totals for one rule — the same two slices the
 *  prototype's `ruleWindowTotals` took. */
export const ruleWindowTotals = (
	window: RuleActivityWindow,
	ruleId: string,
	days: number
) => {
	const entry = window.activity.find((a) => a.ruleId === ruleId)
	const executions = entry?.executions ?? []
	const blockedSources = entry?.blockedSources ?? []
	return {
		triggered: sum(executions.slice(-days)),
		blocked: sum(blockedSources.slice(-days)),
		previous: {
			triggered: sum(executions.slice(-2 * days, -days)),
			blocked: sum(blockedSources.slice(-2 * days, -days))
		}
	}
}
